import React, { useState, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  maxTags?: number;
  id?: string;
  disabled?: boolean;
}

/**
 * ChipInput — tag entry with dedupe, max cap, and suggestion chips.
 * Add via Enter/comma or by clicking a suggestion; remove via the chip's × or
 * Backspace on an empty field. Used for clinician specialty tags (Phase 3c).
 */
const ChipInput: React.FC<ChipInputProps> = ({
  value,
  onChange,
  placeholder = 'Add a tag…',
  suggestions = [],
  maxTags = 10,
  id,
  disabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const atMax = value.length >= maxTags;

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || atMax) return;
    if (value.some((v) => v.toLowerCase() === tag.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  const removeTag = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      removeTag(value.length - 1);
    }
  };

  const filteredSuggestions = draft.trim()
    ? suggestions
        .filter((s) => s.toLowerCase().includes(draft.trim().toLowerCase()))
        .filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))
        .slice(0, 6)
    : [];

  return (
    <div>
      <div
        className={`flex flex-wrap gap-2 items-center rounded-input border border-clinic-primary-lighter bg-white px-3 py-2 transition-colors ${
          disabled ? 'opacity-60' : 'focus-within:ring-2 focus-within:ring-clinic-primary focus-within:border-clinic-primary cursor-text'
        }`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-clinic-primary/10 text-clinic-primary-dark text-sm font-medium pl-3 pr-1.5 py-1"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(idx)}
                aria-label={`Remove ${tag}`}
                className="rounded-full p-0.5 hover:bg-clinic-primary/20 transition-colors"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ))}
        {!atMax && !disabled && (
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={value.length ? '' : placeholder}
            className="flex-1 min-w-[8rem] border-0 p-0 text-sm text-clinic-dark placeholder-clinic-muted focus:ring-0 focus:outline-none"
          />
        )}
      </div>

      {filteredSuggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="rounded-full border border-clinic-primary-lighter px-2.5 py-0.5 text-xs text-clinic-muted hover:border-clinic-primary hover:text-clinic-primary transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}

      <p className="mt-1.5 text-xs text-clinic-muted">
        {atMax
          ? `Maximum ${maxTags} tags.`
          : 'Press Enter or comma to add. Tags power directory search.'}
      </p>
    </div>
  );
};

export default ChipInput;
